import React, { useState } from 'react';
import { BsArrowLeft, BsArrowRight, BsCheckCircleFill, BsCheckLg, BsLink45Deg } from 'react-icons/bs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { submitStorefrontUrl } from './api/api';
import { getApiErrorMessage, isPlausibleStorefrontUrl } from './utils/utils';

type SubmitUrlDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
};

const STEPS = [
  { id: 1, label: 'Enter URL' },
  { id: 2, label: 'Confirm' },
];

const SubmitUrlDialog: React.FC<SubmitUrlDialogProps> = ({ open, onClose, onSubmitted }) => {
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const reset = () => {
    setStep(1);
    setUrl('');
    setUrlError(null);
    setSubmitError(null);
    setSubmitting(false);
    setDone(false);
  };

  const handleClose = () => {
    onClose();
    // Deferred so the closing animation doesn't visibly reset content mid-transition.
    setTimeout(reset, 200);
  };

  const goNext = () => {
    const trimmed = url.trim();
    if (!isPlausibleStorefrontUrl(trimmed)) {
      setUrlError('Enter a valid URL or domain, e.g. https://your-domain.com or *.your-domain.com');
      return;
    }
    setUrlError(null);
    setStep(2);
  };

  const goBack = () => {
    setSubmitError(null);
    setStep(1);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitStorefrontUrl(url.trim());
      setDone(true);
      onSubmitted();
    } catch (err: unknown) {
      setSubmitError(getApiErrorMessage(err, 'Failed to submit URL.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BsLink45Deg /> Submit a URL
          </DialogTitle>
          {!done && (
            <DialogDescription>
              Once approved, payments carrying this URL are allowed to process.
            </DialogDescription>
          )}
        </DialogHeader>

        {done ? (
          <>
            <div className="sfu-dialog-success">
              <BsCheckCircleFill className="sfu-dialog-success-icon" />
              <p>
                <strong>{url}</strong> was submitted for approval. You'll see it in your list with a
                Pending status until an admin reviews it.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" onClick={handleClose}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="sfu-stepper">
              {STEPS.map((s, i) => (
                <React.Fragment key={s.id}>
                  <div className={`sfu-step ${step === s.id ? 'active' : ''} ${step > s.id ? 'done' : ''}`}>
                    <span className="sfu-step-circle">{step > s.id ? <BsCheckLg /> : s.id}</span>
                    <span className="sfu-step-label">{s.label}</span>
                  </div>
                  {i < STEPS.length - 1 && <span className={`sfu-step-line ${step > s.id ? 'done' : ''}`} />}
                </React.Fragment>
              ))}
            </div>

            {step === 1 && (
              <div className="sfu-dialog-body">
                <label className="flex flex-col gap-1.5 text-sm">
                  <span>URL</span>
                  <Input
                    autoFocus
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://your-domain.com"
                    onKeyDown={(e) => e.key === 'Enter' && goNext()}
                  />
                  {urlError && <p className="cm-err">{urlError}</p>}
                </label>
              </div>
            )}

            {step === 2 && (
              <div className="sfu-dialog-body">
                <div className="sfu-confirm-row">
                  <span className="sfu-confirm-label">URL</span>
                  <span className="sfu-confirm-value">{url.trim()}</span>
                </div>
                <p className="cm-field-hint">
                  This will be submitted for admin approval it stays Pending (payments from it are
                  not yet allowed) until approved.
                </p>
                {submitError && (
                  <Alert variant="destructive">
                    <AlertDescription>{submitError}</AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            <DialogFooter>
              {step === 1 ? (
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={goBack} disabled={submitting}>
                  <BsArrowLeft /> Back
                </Button>
              )}
              {step === 1 ? (
                <Button type="button" onClick={goNext}>
                  Next <BsArrowRight />
                </Button>
              ) : (
                <Button type="button" onClick={handleConfirm} disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit for Approval'}
                </Button>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubmitUrlDialog;
